import { Injectable } from '@angular/core';
import { supabase } from '../../core/supabase/supabase.client';

export interface AdminSessionRecord {
  session_id: string;
  is_current: boolean;
  browser: string;
  operating_system: string;
  device_type: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  signed_in_at: string;
  last_seen_at: string;
  metadata_available: boolean;
}

interface DeviceInfo {
  browser: string;
  operatingSystem: string;
  deviceType: AdminSessionRecord['device_type'];
  userAgent: string;
}

@Injectable({ providedIn: 'root' })
export class AdminSessionService {
  async registerCurrentSession(): Promise<void> {
    if (typeof navigator === 'undefined') return;

    const device = parseUserAgent(navigator.userAgent);
    const { error } = await supabase.rpc('register_admin_session', {
      p_browser: device.browser,
      p_operating_system: device.operatingSystem,
      p_device_type: device.deviceType,
      p_user_agent: device.userAgent,
    });

    if (error) throw error;
  }

  async listActiveSessions(): Promise<AdminSessionRecord[]> {
    await this.registerCurrentSession();

    const { data, error } = await supabase.rpc('list_my_admin_sessions');
    if (error) throw error;

    return Array.isArray(data) ? (data as AdminSessionRecord[]) : [];
  }

  async signOutOtherSessions(): Promise<void> {
    const { error } = await supabase.auth.signOut({ scope: 'others' });
    if (error) throw error;
  }
}

export function parseUserAgent(userAgent: string): DeviceInfo {
  const browser = getBrowser(userAgent);
  const operatingSystem = getOperatingSystem(userAgent);
  const deviceType = getDeviceType(userAgent);

  return { browser, operatingSystem, deviceType, userAgent };
}

function getBrowser(userAgent: string): string {
  if (/Edg\//i.test(userAgent)) return 'Microsoft Edge';
  if (/OPR\//i.test(userAgent)) return 'Opera';
  if (/CriOS\//i.test(userAgent)) return 'Google Chrome';
  if (/Chrome\//i.test(userAgent)) return 'Google Chrome';
  if (/FxiOS\//i.test(userAgent)) return 'Mozilla Firefox';
  if (/Firefox\//i.test(userAgent)) return 'Mozilla Firefox';
  if (/Safari\//i.test(userAgent)) return 'Safari';
  return 'Unknown browser';
}

function getOperatingSystem(userAgent: string): string {
  if (/Windows NT/i.test(userAgent)) return 'Windows';
  if (/Android/i.test(userAgent)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'iOS';
  if (/Mac OS X/i.test(userAgent)) return 'macOS';
  if (/Linux/i.test(userAgent)) return 'Linux';
  return 'Unknown system';
}

function getDeviceType(
  userAgent: string
): AdminSessionRecord['device_type'] {
  if (/iPad|Tablet/i.test(userAgent)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android/i.test(userAgent)) return 'mobile';
  return 'desktop';
}
